import { AlertTriangle, Info, Loader2, Lock } from "lucide-react";
import AmountCell from "../revenue/AmountCell";
import { MONTHS, fmtSEK, g } from "../revenue/format";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../ui/tooltip";

function ReadOnlyCell({ cell, missing, muted, onOpen, testId }) {
  return (
    <td className={`px-1 py-1 text-right ${cell.locked && !cell.system ? "bg-slate-100/70" : ""}`} data-testid={testId}>
      <button
        className="h-8 w-full flex items-center justify-end gap-1 pr-1.5 text-sm"
        onClick={onOpen}
        data-testid={`${testId}-btn`}
      >
        {missing && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span tabIndex={0} className="inline-flex" data-testid={`${testId}-missing`}>
                <AlertTriangle className="h-3 w-3 text-[#B45309]" />
              </span>
            </TooltipTrigger>
            <TooltipContent>
              Period closed without verified actual — actual unavailable. Forecast is never substituted for actual.
            </TooltipContent>
          </Tooltip>
        )}
        <span className={muted ? "text-slate-400" : "text-slate-800 font-medium"}>{fmtSEK(cell.value)}</span>
      </button>
    </td>
  );
}

function AccountRow({ account: a, summary, canEdit, onCommit, onOpenDetail }) {
  return (
    <tr className={`border-b border-slate-100 ${a.active ? "" : "opacity-50"}`} data-testid={`ic-account-row-${a.account_number}`}>
      <td className="py-1 px-3 sticky left-0 bg-white z-10">
        <div className="flex items-center gap-2">
          <button
            className="text-left"
            onClick={() => onOpenDetail(a.id, 1)}
            data-testid={`ic-open-account-${a.id}`}
          >
            <span className="text-sm font-semibold text-[#0052CC] hover:underline">{a.account_number}</span>
            {a.description && <span className="text-xs text-slate-500 block">{a.description}</span>}
          </button>
          {a.missing_assumptions?.length > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0} className="inline-flex" data-testid={`ic-assumption-warning-${a.id}`}>
                  <AlertTriangle className="h-3.5 w-3.5 text-[#B45309]" />
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {a.missing_assumptions.includes("PAYMENT_TERMS") && <div>Missing payment terms assumption for account {a.account_number} — required for Build 7 cash timing.</div>}
                {a.missing_assumptions.includes("VAT") && <div>Missing VAT assumption for account {a.account_number} — required for Build 7 VAT handling.</div>}
                <div className="text-xs opacity-80 mt-1">Forecasting is not blocked. Maintain under Administration → Cost Accounts.</div>
              </TooltipContent>
            </Tooltip>
          )}
          {!a.active && <span className="text-[10px] font-semibold text-slate-400 uppercase">inactive</span>}
        </div>
      </td>
      {MONTHS.map((_, i) => {
        const m = i + 1;
        const cell = g(a.months, m) || {};
        if (cell.locked || !canEdit || cell.actual_source === "IMPORTED_ACTUAL") {
          return (
            <ReadOnlyCell
              key={m}
              cell={cell}
              missing={cell.missing_actual}
              muted={!a.active}
              onOpen={() => onOpenDetail(a.id, m)}
              testId={`ic-cell-${a.id}-${m}`}
            />
          );
        }
        return (
          <AmountCell
            key={m}
            value={cell.forecast_amount}
            locked={false}
            canEdit
            onCommit={(v) => onCommit(a.id, m, v)}
            testId={`ic-cell-${a.id}-${m}`}
          />
        );
      })}
      <td className="py-1 px-2 text-right text-xs text-slate-500" data-testid={`ic-ytd-${a.id}`}>
        {summary?.has_closed_months ? fmtSEK(a.ytd) : "—"}
      </td>
      <td className="py-1 px-2 text-right text-sm font-semibold text-slate-800" data-testid={`ic-fy-${a.id}`}>{fmtSEK(a.fy)}</td>
    </tr>
  );
}

function SystemRow({ account: a, onOpenDetail }) {
  return (
    <tr className="border-b border-slate-200 bg-[#F8FAFC]" data-testid="ic-system-row-6591">
      <td className="py-2.5 px-3 sticky left-0 bg-[#F8FAFC] z-10">
        <div className="flex items-center gap-2">
          <button className="text-left" onClick={() => onOpenDetail(a.id, 1)} data-testid="ic-open-account-6591">
            <span className="text-sm font-bold text-slate-900">{a.account_number} {a.description || "Group Overhead"}</span>
          </button>
          <span className="text-[10px] font-semibold text-slate-400 uppercase">System</span>
          <Tooltip>
            <TooltipTrigger asChild>
              <span tabIndex={0} className="inline-flex" data-testid="ic-system-info-6591">
                <Info className="h-3.5 w-3.5 text-slate-400" />
              </span>
            </TooltipTrigger>
            <TooltipContent>
              Reserved for Group Overhead — calculated in the Group build (Build 8). Monthly values are not manually editable.
            </TooltipContent>
          </Tooltip>
        </div>
      </td>
      {MONTHS.map((_, i) => {
        const m = i + 1;
        const cell = g(a.months, m) || {};
        return (
          <td key={m} className="py-2.5 px-1 text-right" data-testid={`ic-sys-cell-${m}`}>
            <button
              className="h-8 w-full flex items-center justify-end pr-1.5 text-sm text-slate-500"
              onClick={() => onOpenDetail(a.id, m)}
              data-testid={`ic-sys-cell-${m}-btn`}
            >
              {cell.value == null ? "—" : fmtSEK(cell.value)}
            </button>
          </td>
        );
      })}
      <td className="py-2.5 px-2 text-right text-sm text-slate-400">—</td>
      <td className="py-2.5 px-2 text-right text-sm text-slate-400" data-testid="ic-sys-fy">N/A</td>
    </tr>
  );
}

function TotalRow({ data, summary }) {
  return (
    <tr className="border-t-2 border-slate-300 bg-white" data-testid="ic-regular-total-row">
      <td className="py-3 px-3 sticky left-0 bg-white z-10">
        <span className="text-sm font-extrabold text-slate-900">REGULAR INDIRECT COSTS</span>
      </td>
      {MONTHS.map((_, i) => {
        const m = i + 1;
        const reg = g(data.months, m)?.regular || {};
        const closed = g(data.months, m)?.closed;
        return (
          <td key={m} className={`py-3 px-1 text-right ${closed ? "bg-slate-100/70" : ""}`} data-testid={`ic-total-cell-${m}`}>
            <div className="h-8 flex items-center justify-end gap-1 pr-1.5 text-sm font-extrabold text-slate-900">
              {reg.incomplete && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={0} className="inline-flex" data-testid={`ic-total-incomplete-${m}`}>
                      <AlertTriangle className="h-3 w-3 text-[#B45309]" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    Missing actual for account(s): {(reg.missing_actual || []).join(", ")}
                  </TooltipContent>
                </Tooltip>
              )}
              {fmtSEK(reg.value)}
            </div>
          </td>
        );
      })}
      <td className="py-3 px-2 text-right text-sm font-semibold text-slate-700" data-testid="ic-total-ytd">
        {summary?.has_closed_months ? fmtSEK(summary.ytd_actual) : "—"}
      </td>
      <td className="py-3 px-2 text-right text-sm font-extrabold text-slate-900" data-testid="ic-total-fy">
        {fmtSEK(summary?.expected_fy_regular)}
      </td>
    </tr>
  );
}

function GroupOverheadRow({ data, summary }) {
  return (
    <tr className="border-b border-slate-100 bg-white" data-testid="ic-go-row">
      <td className="py-2.5 px-3 sticky left-0 bg-white z-10">
        <span className="text-sm font-semibold text-slate-600">Group Overhead (6591)</span>
      </td>
      {MONTHS.map((_, i) => {
        const m = i + 1;
        const go = g(data.months, m)?.group_overhead || {};
        return (
          <td key={m} className="py-2.5 px-1 text-right" data-testid={`ic-go-cell-${m}`}>
            <div className="h-8 flex items-center justify-end pr-1.5 text-sm text-slate-600">
              {go.value == null ? "—" : fmtSEK(go.value)}
            </div>
          </td>
        );
      })}
      <td className="py-2.5 px-2 text-right text-sm text-slate-400">—</td>
      <td className="py-2.5 px-2 text-right text-sm font-semibold text-slate-600" data-testid="ic-go-fy">
        {summary?.expected_fy_group_overhead == null ? "Not calculated" : fmtSEK(summary.expected_fy_group_overhead)}
      </td>
    </tr>
  );
}

export default function IndirectGrid({ data, loading, visibleAccounts, canEdit, onCommit, onOpenDetail }) {
  const regular = visibleAccounts.filter((a) => !a.system);
  const system = visibleAccounts.filter((a) => a.system);
  // Build 8.5: month-level ACT marker (same visual language as the P&L month
  // headers) for months where a committed accounting import is authoritative.
  const actualMonths = new Set();
  if (data) {
    for (const a of data.accounts || []) {
      for (let m = 1; m <= 12; m++) {
        if (g(a.months, m)?.actual_source === "IMPORTED_ACTUAL") actualMonths.add(m);
      }
    }
  }
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-auto max-h-[calc(100vh-16rem)]" data-testid="ic-grid">
      {loading && (
        <div className="py-16 flex justify-center" data-testid="ic-loading">
          <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
        </div>
      )}
      {!loading && data && (
        <table className="w-full min-w-[1300px] border-collapse">
          <thead>
            <tr className="bg-secondary/50 border-b border-slate-200">
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-2.5 px-3 sticky left-0 top-0 bg-secondary/50 min-w-64 z-30 shadow-[0_2px_4px_-1px_rgba(15,23,42,0.06)]">Cost Account</th>
              {MONTHS.map((label, i) => {
                const closed = g(data.periods, i + 1)?.status === "CLOSED";
                return (
                  <th key={label} className={`text-right text-xs font-semibold uppercase tracking-wider py-2.5 px-1 w-24 sticky top-0 z-20 shadow-[0_2px_4px_-1px_rgba(15,23,42,0.06)] ${closed ? "text-slate-400 bg-slate-100" : "text-slate-500 bg-slate-50"}`}>
                    <div className="flex items-center justify-end gap-1 pr-1.5">
                      {closed && <Lock className="h-3 w-3" />}
                      {actualMonths.has(i + 1) && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span tabIndex={0} className="inline-flex items-center rounded bg-[#0052CC] text-white text-[9px] font-bold px-1 py-px" data-testid={`ic-month-actual-${i + 1}`}>
                              ACT
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            Accounting actuals — committed P&L import (Build 8.5)
                          </TooltipContent>
                        </Tooltip>
                      )}
                      {label}
                    </div>
                  </th>
                );
              })}
              <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider py-2.5 px-2 w-24 sticky top-0 z-20 bg-secondary/50 shadow-[0_2px_4px_-1px_rgba(15,23,42,0.06)]">YTD</th>
              <th className="text-right text-xs font-semibold text-slate-500 uppercase tracking-wider py-2.5 px-2 w-28 sticky top-0 z-20 bg-secondary/50 shadow-[0_2px_4px_-1px_rgba(15,23,42,0.06)]">Expected FY</th>
            </tr>
          </thead>
          <tbody>
            {regular.map((a) => (
              <AccountRow
                key={a.id}
                account={a}
                summary={data.summary}
                canEdit={canEdit}
                onCommit={onCommit}
                onOpenDetail={onOpenDetail}
              />
            ))}
            {regular.length === 0 && (
              <tr className="border-b border-slate-100">
                <td colSpan={14} className="py-3 px-3 text-xs text-slate-400" data-testid="ic-no-accounts">
                  No plannable cost accounts — maintain accounts under Administration → Cost Accounts.
                </td>
              </tr>
            )}
            {system.map((a) => (
              <SystemRow key={a.id} account={a} onOpenDetail={onOpenDetail} />
            ))}
            <TotalRow data={data} summary={data.summary} />
            <GroupOverheadRow data={data} summary={data.summary} />
          </tbody>
        </table>
      )}
    </div>
  );
}
